/**
 * API de listagem de cursos, categorias e status.
 * Usado em b2b e b2b2c com o api client de cada app.
 * Integração com v1/Course, v1/CourseCategory e v1/Course/status.
 */

export interface Course {
  id: string;
  name: string;
  description?: string;
  [key: string]: unknown;
}

/** Item retornado por GET v1/CourseCategory */
export interface CourseCategoryTranslation {
  languageCode: string;
  name: string;
}

export interface CourseCategory {
  id: string;
  isActive: boolean;
  translations: CourseCategoryTranslation[];
}

/** Item retornado por GET v1/Course/status */
export interface CourseStatus {
  id: number;
  name: string;
}

export interface FetchCoursesParams {
  page: number;
  limit: number;
  /** Enviado como CourseTitle na API */
  search?: string;
  /** Enviado como CourseCategoryId (UUID) na API */
  courseCategoryId?: string;
  /** Enviado como Status (nome, ex: Draft) na API */
  status?: string;
  /** Enviado como DateCreated (YYYY-MM-DD) na API */
  dateCreated?: string;
  /**
   * Ordenação por data de criação (mais recente primeiro).
   * Enviado como SortColumn=CreatedAt e SortDirection=desc em v1/Course/pagination.
   */
  orderByCreatedAtDesc?: boolean;
}

export interface FetchCoursesResponse {
  data: Course[];
  total: number;
}

export type FetchCoursesFn = (
  params: FetchCoursesParams
) => Promise<FetchCoursesResponse>;

/** Parâmetros para listagem de categorias (GET v1/CourseCategory) */
export interface FetchCourseCategoriesParams {
  /** Incluir categorias inativas (default: false) */
  includeInactive?: boolean;
}

export type FetchCourseCategoriesFn = (
  params?: FetchCourseCategoriesParams
) => Promise<CourseCategory[]>;

export type FetchCourseStatusFn = () => Promise<CourseStatus[]>;

/** Item de tradução no body de POST v1/Course */
export interface CourseTranslationRequest {
  languageCode: string;
  title: string;
  description: string;
}

/** Body de POST v1/Course (criar curso) */
export interface CreateCourseRequest {
  translations: CourseTranslationRequest[];
  courseCategoryId: string;
  tags: string;
  duration: number;
  /** Data efetiva (ISO). `null` quando o usuário deixa o campo opcional vazio — a API deve receber a chave para limpar/ignorar. */
  effectiveDate: string | null;
  difficultyLevel: number;
}

/** Body de PATCH v1/Course/{courseId}/status */
export interface UpdateCourseStatusRequest {
  status: number;
}

/** Resposta de POST v1/Course (ex.: id do curso; API pode retornar text/plain ou JSON). */
export type CreateCourseResponse = string | { id?: string } | unknown;

export type CreateCourseFn = (
  body: CreateCourseRequest
) => Promise<CreateCourseResponse>;

/** Atualiza um curso existente (PUT v1/Course/{courseId}). Mesmo body do create. */
export type UpdateCourseFn = (
  courseId: string,
  body: CreateCourseRequest
) => Promise<unknown>;

/** Altera apenas o status do curso (PATCH v1/Course/{courseId}/status). */
export type UpdateCourseStatusFn = (
  courseId: string,
  body: UpdateCourseStatusRequest
) => Promise<unknown>;

/** Arquiva um curso existente (PATCH v1/Course/{courseId}/archive). */
export type ArchiveCourseFn = (courseId: string) => Promise<unknown>;

/** Upload da capa: só chamar após sucesso de createCourse. */
export type UploadCourseCoverFn = (
  courseId: string,
  file: File
) => Promise<unknown>;

/** Item de tradução retornado por GET v1/Course/{id} */
export interface CourseDetailTranslation {
  languageCode: string;
  title: string;
  description: string;
}

/** Item de usuário no público-alvo (GET v1/Course/{id} ou audience). */
export interface CourseAudienceUser {
  userId: string;
  name: string;
}

/** Item de grupo no público-alvo (GET v1/Course/{id} ou audience). */
export interface CourseAudienceUserGroup {
  userGroupId: string;
  name: string;
}

/** Resposta de GET v1/Course/{id} (detalhes do curso para Step 1 / edição). */
export interface CourseDetail {
  id: string;
  translations: CourseDetailTranslation[];
  courseCategoryId: string;
  tags: string;
  coverImageUrl: string | null;
  duration: number;
  effectiveDate: string | null;
  status: number;
  statusName: string;
  difficultyLevel: number;
  difficultyLevelName: string;
  /** Público-alvo: usuários atribuídos ao curso (retornado pela API na edição). */
  audienceUsers?: CourseAudienceUser[];
  /** Público-alvo: grupos atribuídos ao curso (retornado pela API na edição). */
  audienceUserGroups?: CourseAudienceUserGroup[];
}

export type FetchCourseByIdFn = (courseId: string) => Promise<CourseDetail>;

/** Parâmetros para GET v1/Course/{courseId}/course-module */
export interface FetchCourseModulesParams {
  /** Incluir módulos inativos (default: false) */
  includeInactive?: boolean;
}

/** Tradução de módulo/lição retornada por GET course-module */
export interface CourseModuleTranslationItem {
  languageCode: string;
  title: string;
}

/** Item do array courseModuleLessonFiles (id usado no DELETE course-module-lesson-files). */
export interface CourseModuleLessonFileDetail {
  id: string;
  courseModuleLessonId?: string;
  fileUrl?: string;
  blobName?: string;
  originalFileName?: string;
  containerName?: string;
  sizeInBytes?: number;
  contentType?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Lição retornada por GET v1/Course/{courseId}/course-module (courseModuleLessons) */
export interface CourseModuleLessonDetail {
  id: string;
  courseModuleId: string;
  type: number;
  typeName: string;
  videoUrl?: string;
  points?: number;
  displayOrder: number;
  translations: CourseModuleTranslationItem[];
  courseModuleLessonFiles?: CourseModuleLessonFileDetail[];
  courseModuleLessonQuizConfig?: ModuleLessonQuizConfigRequest | CourseModuleLessonQuizConfigDetail | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Módulo retornado por GET v1/Course/{courseId}/course-module */
export interface CourseModuleDetail {
  id: string;
  courseId: string;
  isActive: boolean;
  displayOrder: number;
  translations: CourseModuleTranslationItem[];
  createdAt?: string;
  lessonsCount?: number;
  courseModuleLessons: CourseModuleLessonDetail[];
}

export type FetchCourseModulesFn = (
  courseId: string,
  params?: FetchCourseModulesParams
) => Promise<CourseModuleDetail[]>;

/** Cliente mínimo necessário (get, post, put, delete). Compatível com ApiClient do core. */
export interface CoursesListApiClient {
  get<T>(url: string): Promise<T>;
  post<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
  put<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
  patch<T>(url: string, data?: unknown, config?: unknown): Promise<T>;
  delete<T>(url: string, config?: unknown): Promise<T>;
}

/** Item retornado por GET v1/Course/pagination (resposta da API Feraah) */
export interface CoursePaginationItem {
  courseId: string;
  /**
   * Formato antigo da API: `courseTitle`.
   * Formato novo da API: `translations[]` (mantivemos como opcional para compatibilidade).
   */
  courseTitle?: string;
  /** Título por idioma (formato novo da API). */
  translations?: { languageCode: string; title: string }[];
  category: string;
  status: number;
  statusName: string;
  createdAt: string;
  created: string;
  modulesQty: number;
  enrolled: number;
  completion: number;
}

/** Resposta bruta de GET v1/Course/pagination */
export interface CoursePaginationResponse {
  courses: CoursePaginationItem[];
  totalRecords: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
}

const COURSE_PATH = 'v1/Course';
const COURSE_PAGINATION_PATH = 'v1/Course/pagination';
const COURSE_CATEGORY_PATH = 'v1/CourseCategory';
const COURSE_STATUS_PATH = 'v1/Course/status';
const COURSE_MODULE_PATH = 'v1/course-module';
const COURSE_MODULE_LESSON_PATH = 'v1/course-module-lesson';
const COURSE_MODULE_LESSON_FILES_PATH = 'v1/course-module-lesson-files';
const COURSE_MODULE_LESSON_QUIZ_CONFIG_PATH = 'v1/course-module-lesson-quiz-config';
const TARGETING_USERS_PAGINATION_PATH = 'v1/User/pagination';
const TARGETING_GROUPS_PAGINATION_PATH = 'v1/UserGroup/pagination';
const TARGETING_PAGE_SIZE = 5;

/** Tradução no body de POST v1/Course/{courseId}/module */
export interface ModuleTranslationRequest {
  languageCode: string;
  title: string;
}

/** Body de POST v1/Course/{courseId}/module (criar módulo) */
export interface CreateModuleRequest {
  translations: ModuleTranslationRequest[];
}

/** Body de PUT v1/course-module/{moduleId} (atualizar módulo) */
export interface UpdateModuleRequest {
  translations: ModuleTranslationRequest[];
  isActive: boolean;
  displayOrder: number;
}

/** Resposta de POST v1/Course/{courseId}/module (módulo criado; guardar id para CourseModuleLesson) */
export interface CourseModule {
  id: string;
  courseId: string;
  isActive: boolean;
  displayOrder: number;
  translations: ModuleTranslationRequest[];
}

export type CreateModuleFn = (
  courseId: string,
  body: CreateModuleRequest
) => Promise<CourseModule>;

/** Atualiza um módulo existente (PUT v1/course-module/{moduleId}). */
export type UpdateModuleFn = (
  moduleId: string,
  body: UpdateModuleRequest
) => Promise<unknown>;

/** Item do body de PATCH display-order (módulos ou lições). */
export interface DisplayOrderItem {
  id: string;
  displayOrder: number;
}

/** Atualiza ordenação de módulos em lote (PATCH v1/course-module/display-order). Body: [{ id, displayOrder }]. */
export type UpdateCourseModulesDisplayOrderFn = (
  items: DisplayOrderItem[]
) => Promise<unknown>;

/** Atualiza ordenação de lições em lote (PATCH v1/course-module-lesson/display-order). Body: [{ id, displayOrder }]. Todos os itens devem pertencer ao mesmo módulo; chame uma vez por módulo. */
export type UpdateCourseModuleLessonsDisplayOrderFn = (
  items: DisplayOrderItem[]
) => Promise<unknown>;

/** Tradução no body de POST v1/course-module/{moduleId}/lesson */
export interface ModuleLessonTranslationRequest {
  languageCode: string;
  title: string;
}

/** Tipo de lição: 1 = Video, 2 = File, 3 = Quiz (ou conforme API) */
export type LessonType = 1 | 2 | 3;

/** Body de POST v1/course-module/{moduleId}/lesson (vídeo, file ou quiz) */
export interface CreateModuleLessonRequest {
  type: LessonType;
  /** URL do vídeo (ex.: Vimeo) quando type === 1 */
  videoUrl?: string;
  /** Pontos da lição */
  points?: number;
  translations: ModuleLessonTranslationRequest[];
  /** Configuração do quiz quando type === 3 */
  quizConfig?: ModuleLessonQuizConfigRequest;
}

/** Resposta de POST v1/course-module/{moduleId}/lesson */
export interface CourseModuleLesson {
  id: string;
  courseModuleId: string;
  type: LessonType;
  typeName: string;
  videoUrl?: string;
  points?: number;
  displayOrder: number;
  translations: ModuleLessonTranslationRequest[];
}

export type CreateModuleLessonFn = (
  courseModuleId: string,
  body: CreateModuleLessonRequest
) => Promise<CourseModuleLesson>;

/** Configuração de quiz no body de PUT v1/course-module-lesson/{lessonId} */
export interface ModuleLessonQuizConfigRequest {
  isManual: boolean;
  qtyQuestionLow: number;
  qtyQuestionMedium: number;
  qtyQuestionHard: number;
  isCourseFinalExam: boolean;
}

/** Item de courseModuleLessonQuestions retornado por GET course-module-lesson (resposta da API). */
export interface CourseModuleLessonQuestionItem {
  id?: string;
  courseModuleLessonQuizConfigId?: string;
  questionId: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Configuração de quiz retornada por GET course-module / GET course-module-lesson (resposta da API). */
export interface CourseModuleLessonQuizConfigDetail {
  id?: string;
  courseModuleLessonId?: string;
  isManual: boolean;
  qtyQuestionLow: number;
  qtyQuestionMedium: number;
  qtyQuestionHard: number;
  isCourseFinalExam: boolean;
  courseModuleLessonQuestions?: CourseModuleLessonQuestionItem[];
  createdAt?: string;
  updatedAt?: string;
}

/** Body de PUT v1/course-module-lesson/{lessonId} (atualizar lição) */
export interface UpdateModuleLessonRequest {
  type: LessonType;
  videoUrl?: string;
  points?: number;
  translations: ModuleLessonTranslationRequest[];
  quizConfig?: ModuleLessonQuizConfigRequest;
}

export type UpdateModuleLessonFn = (
  lessonId: string,
  body: UpdateModuleLessonRequest
) => Promise<unknown>;

export type UpdateQuizConfigQuestionsFn = (
  quizConfigId: string,
  questionIds: string[]
) => Promise<unknown>;

export interface UpdateCourseAudienceRequest {
  userIds: string[];
  userGroupIds: string[];
}

export type UpdateCourseAudienceFn = (
  courseId: string,
  body: UpdateCourseAudienceRequest
) => Promise<unknown>;
export interface TargetingGroup {
  id: string;
  name: string;
  type: string;
  members: number;
  selected: boolean;
}

export interface TargetingUser {
  id: string;
  name: string;
  role: string;
  location: string;
  selected: boolean;
}

export interface FetchTargetingGroupsParams {
  page: number;
  search?: string;
}

export interface FetchTargetingUsersParams {
  page: number;
  search?: string;
}

export interface FetchTargetingGroupsResponse {
  data: TargetingGroup[];
  total: number;
}

export interface FetchTargetingUsersResponse {
  data: TargetingUser[];
  total: number;
}

export type FetchTargetingGroupsFn = (
  params: FetchTargetingGroupsParams
) => Promise<FetchTargetingGroupsResponse>;

export type FetchTargetingUsersFn = (
  params: FetchTargetingUsersParams
) => Promise<FetchTargetingUsersResponse>;

interface TargetingUserPaginationApiItem {
  id: string;
  name: string | null;
  email: string;
  roles?: Array<{ id: string; name: string }>;
}

interface TargetingUserPaginationApiResponse {
  users: TargetingUserPaginationApiItem[];
  totalRecords: number;
}

interface TargetingGroupPaginationApiItem {
  id: string;
  name: string;
  qtdUsers?: number;
}

interface TargetingGroupPaginationApiResponse {
  userGroups: TargetingGroupPaginationApiItem[];
  totalRecords: number;
}

function normalizeKeySegment(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function mapPaginationItemToCourse(item: CoursePaginationItem): Course {
  // A API pode retornar `courseTitle` (antigo) ou `translations[]` (novo).
  // Sem contexto de idioma no core, usamos o primeiro idioma como fallback.
  const title = item.courseTitle ?? item.translations?.[0]?.title ?? '';
  return {
    id: item.courseId,
    name: title,
    translations: item.translations,
    category: item.category,
    categoryName: item.category,
    status: item.status,
    statusName: item.statusName,
    createdAt: item.createdAt,
    created: item.created,
    modulesQty: item.modulesQty,
    modules: item.modulesQty,
    modulesCount: item.modulesQty,
    enrolled: item.enrolled,
    enrolledCount: item.enrolled,
    completion: item.completion,
    completionPercent: item.completion,
  };
}

function normalizePaginationResponse(
  raw: CoursePaginationResponse
): FetchCoursesResponse {
  const courses = raw.courses ?? [];
  return {
    data: courses.map(mapPaginationItemToCourse),
    total: raw.totalRecords ?? 0,
  };
}

/**
 * Cria o cliente de API de cursos (listagem, categorias, status, criar curso e upload de capa).
 * Recebe o api do app (b2b/b2b2c) e retorna { fetchCourses, fetchCourseCategories, fetchCourseStatus, createCourse, uploadCourseCover }.
 */
export function createCoursesApi(api: CoursesListApiClient) {
  return {
    /**
     * Lista cursos com paginação (GET v1/Course/pagination).
     * Requer Authorization. Retorno compatível com o componente Courses do shared.
     */
    fetchCourses(params: FetchCoursesParams): Promise<FetchCoursesResponse> {
      const {
        page,
        limit,
        search,
        courseCategoryId,
        status,
        dateCreated,
        orderByCreatedAtDesc,
      } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('pageNumber', String(page));
      searchParams.set('pageSize', String(limit));
      if (search && search.trim())
        searchParams.set('CourseTitle', search.trim());
      if (courseCategoryId && courseCategoryId.trim())
        searchParams.set('CourseCategoryId', courseCategoryId.trim());
      if (status && status.trim()) searchParams.set('Status', status.trim());
      if (dateCreated && dateCreated.trim())
        searchParams.set('DateCreated', dateCreated.trim());
      if (orderByCreatedAtDesc) {
        searchParams.set('SortColumn', 'CreatedAt');
        searchParams.set('SortDirection', 'desc');
      }

      const query = searchParams.toString();
      const url = `/${COURSE_PAGINATION_PATH}?${query}`;

      return api
        .get<CoursePaginationResponse>(url)
        .then(normalizePaginationResponse)
        .catch(() => ({ data: [], total: 0 }));
    },

    /**
     * Lista categorias de curso (GET v1/CourseCategory?includeInactive=false).
     */
    async fetchCourseCategories(
      params?: FetchCourseCategoriesParams
    ): Promise<CourseCategory[]> {
      const includeInactive = params?.includeInactive ?? false;
      const query = new URLSearchParams();
      query.set('includeInactive', String(includeInactive));
      const url = `/${COURSE_CATEGORY_PATH}?${query.toString()}`;
      try {
        const data = await api.get<CourseCategory[]>(url);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },

    /**
     * Lista status de curso (GET v1/Course/status). Requer Authorization.
     */
    async fetchCourseStatus(): Promise<CourseStatus[]> {
      try {
        const data = await api.get<CourseStatus[]>(`/${COURSE_STATUS_PATH}`);
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    },

    /**
     * Consulta detalhes de um curso (GET v1/Course/{id}). Requer Authorization.
     * Usado para preencher o formulário do Step 1 na edição.
     */
    async fetchCourseById(courseId: string): Promise<CourseDetail> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}`;
      return api.get<CourseDetail>(url);
    },

    /**
     * Lista módulos do curso com lições (GET v1/Course/{courseId}/course-module).
     * Requer Authorization. Usado para preencher o Step 2 (currículo) na edição.
     */
    async fetchCourseModules(
      courseId: string,
      params?: FetchCourseModulesParams
    ): Promise<CourseModuleDetail[]> {
      const includeInactive = params?.includeInactive ?? false;
      const query = new URLSearchParams();
      query.set('includeInactive', String(includeInactive));
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}/course-module?${query.toString()}`;
      const data = await api.get<CourseModuleDetail[]>(url);
      return Array.isArray(data) ? data : [];
    },

    /**
     * Consulta uma lição por id (GET v1/course-module-lesson/{lessonId}).
     * Usado para carregar dados frescos ao abrir o modal de edição de quiz.
     */
    async fetchModuleLesson(lessonId: string): Promise<CourseModuleLessonDetail> {
      const url = `/${COURSE_MODULE_LESSON_PATH}/${encodeURIComponent(lessonId)}`;
      return api.get<CourseModuleLessonDetail>(url);
    },

    /**
     * Cria um curso (POST v1/Course). Requer Authorization.
     * Body conforme CreateCourseRequest (translations, courseCategoryId, tags, duration, effectiveDate, difficultyLevel).
     */
    async createCourse(
      body: CreateCourseRequest
    ): Promise<CreateCourseResponse> {
      return api.post<CreateCourseResponse>(`/${COURSE_PATH}`, body);
    },

    /**
     * Atualiza um curso existente (PUT v1/Course/{courseId}). Requer Authorization.
     * Body igual ao create: translations, courseCategoryId, tags, duration, effectiveDate, difficultyLevel.
     */
    async updateCourse(
      courseId: string,
      body: CreateCourseRequest
    ): Promise<unknown> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}`;
      return api.put<unknown>(url, body);
    },

    /**
     * Atualiza o status do curso (PATCH v1/Course/{courseId}/status). Requer Authorization.
     * Body: { status: number } (ids conforme GET v1/Course/status).
     */
    async updateCourseStatus(
      courseId: string,
      body: UpdateCourseStatusRequest
    ): Promise<unknown> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}/status`;
      return api.patch<unknown>(url, body);
    },

    /**
     * Arquiva um curso existente (PATCH v1/Course/{courseId}/archive). Requer Authorization.
     */
    async archiveCourse(courseId: string): Promise<unknown> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}/archive`;
      return api.patch<unknown>(url);
    },

    /**
     * Envia a imagem de capa do curso (POST v1/Course/{courseId}/cover).
     * Só deve ser chamado após createCourse retornar com sucesso e haver um courseId.
     * Body: multipart/form-data com campo CoverImage (File).
     */
    async uploadCourseCover(courseId: string, file: File): Promise<unknown> {
      const formData = new FormData();
      formData.append('CoverImage', file);
      return api.post<unknown>(
        `/${COURSE_PATH}/${courseId}/cover`,
        formData,
        {}
      );
    },

    /**
     * Lista grupos para targeting no Step 3 (GET v1/UserGroup/pagination).
     * PageSize fixo 5 e includeInactive=false.
     */
    async fetchTargetingGroups(
      params: FetchTargetingGroupsParams
    ): Promise<FetchTargetingGroupsResponse> {
      const { page, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('PageNumber', String(page));
      searchParams.set('PageSize', String(TARGETING_PAGE_SIZE));
      searchParams.set('includeInactive', String(false));
      if (search && search.trim()) searchParams.set('Name', search.trim());

      const url = `/${TARGETING_GROUPS_PAGINATION_PATH}?${searchParams.toString()}`;
      try {
        const raw = await api.get<TargetingGroupPaginationApiResponse>(url);
        const items = Array.isArray(raw.userGroups) ? raw.userGroups : [];
        return {
          data: items.map(item => ({
            id: item.id,
            name: item.name,
            type: 'branch',
            members: typeof item.qtdUsers === 'number' ? item.qtdUsers : 0,
            selected: false,
          })),
          total: typeof raw.totalRecords === 'number' ? raw.totalRecords : 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },

    /**
     * Lista usuários para targeting no Step 3 (GET v1/User/pagination).
     * PageSize fixo 5 e includeInactive=false.
     */
    async fetchTargetingUsers(
      params: FetchTargetingUsersParams
    ): Promise<FetchTargetingUsersResponse> {
      const { page, search } = params;
      const searchParams = new URLSearchParams();
      searchParams.set('pageNumber', String(page));
      searchParams.set('pageSize', String(TARGETING_PAGE_SIZE));
      searchParams.set('includeInactive', String(false));
      if (search && search.trim()) searchParams.set('SearchText', search.trim());

      const url = `/${TARGETING_USERS_PAGINATION_PATH}?${searchParams.toString()}`;
      try {
        const raw = await api.get<TargetingUserPaginationApiResponse>(url);
        const items = Array.isArray(raw.users) ? raw.users : [];
        return {
          data: items.map(item => {
            const primaryRole =
              item.roles?.[0]?.name != null ? String(item.roles[0].name) : '';
            return {
              id: item.id,
              name: (item.name ?? item.email ?? '').trim(),
              role: primaryRole ? normalizeKeySegment(primaryRole) : '',
              location: '',
              selected: false,
            };
          }),
          total: typeof raw.totalRecords === 'number' ? raw.totalRecords : 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },

    /**
     * Atualiza o público-alvo do curso (PUT v1/Course/{courseId}/audience).
     * Body: { userIds: string[]; userGroupIds: string[] }.
     */
    async updateCourseAudience(
      courseId: string,
      body: UpdateCourseAudienceRequest
    ): Promise<unknown> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}/audience`;
      return api.put<unknown>(url, body);
    },

    /**
     * Cria um módulo no curso (POST v1/Course/{courseId}/module).
     * Requer Authorization. Retorna o módulo com id, courseId, displayOrder e translations.
     * Guardar o id retornado para adicionar lições via createModuleLesson.
     */
    async createModule(
      courseId: string,
      body: CreateModuleRequest
    ): Promise<CourseModule> {
      const url = `/${COURSE_PATH}/${encodeURIComponent(courseId)}/module`;
      return api.post<CourseModule>(url, body);
    },

    /**
     * Atualiza um módulo existente (PUT v1/course-module/{moduleId}).
     * Body: translations, isActive, displayOrder.
     */
    async updateModule(
      moduleId: string,
      body: UpdateModuleRequest
    ): Promise<unknown> {
      const url = `/${COURSE_MODULE_PATH}/${encodeURIComponent(moduleId)}`;
      return api.put<unknown>(url, body);
    },

    /**
     * Adiciona uma lição ao módulo (POST v1/course-module/{courseModuleId}/lesson).
     * Requer Authorization. Suporta type 1 (Video), 2 (File), 3 (Quiz).
     * Para vídeo: informar videoUrl e opcionalmente points. Retorna a lição criada com id.
     */
    async createModuleLesson(
      courseModuleId: string,
      body: CreateModuleLessonRequest
    ): Promise<CourseModuleLesson> {
      const url = `/${COURSE_MODULE_PATH}/${encodeURIComponent(courseModuleId)}/lesson`;
      return api.post<CourseModuleLesson>(url, body);
    },

    /**
     * Atualiza uma lição existente (PUT v1/course-module-lesson/{lessonId}).
     * Body: type, videoUrl, points, translations, quizConfig (opcional).
     */
    async updateModuleLesson(
      lessonId: string,
      body: UpdateModuleLessonRequest
    ): Promise<unknown> {
      const url = `/${COURSE_MODULE_LESSON_PATH}/${encodeURIComponent(lessonId)}`;
      return api.put<unknown>(url, body);
    },

    /**
     * Atualiza a ordenação de módulos em lote (PATCH v1/course-module/display-order).
     * Body: array [{ id, displayOrder }]. Chamar após POST/PUT de módulos.
     */
    async updateCourseModulesDisplayOrder(
      items: DisplayOrderItem[]
    ): Promise<unknown> {
      const url = `/${COURSE_MODULE_PATH}/display-order`;
      return api.patch<unknown>(url, items);
    },

    /**
     * Atualiza a ordenação de lições em lote (PATCH v1/course-module-lesson/display-order).
     * Body: array [{ id, displayOrder }]. Chamar após POST/PUT de lições.
     */
    async updateCourseModuleLessonsDisplayOrder(
      items: DisplayOrderItem[]
    ): Promise<unknown> {
      const url = `/${COURSE_MODULE_LESSON_PATH}/display-order`;
      return api.patch<unknown>(url, items);
    },

    /**
     * Remove um arquivo anexado à lição (DELETE v1/course-module-lesson-files/{fileId}).
     * O fileId é o id do item em courseModuleLessonFiles retornado pelo GET course-module.
     */
    async deleteCourseModuleLessonFile(fileId: string): Promise<unknown> {
      const url = `/${COURSE_MODULE_LESSON_FILES_PATH}/${encodeURIComponent(fileId)}`;
      return api.delete<unknown>(url);
    },

    /**
     * Envia um arquivo para a lição (POST v1/course-module-lesson/{lessonId}/files).
     * Body: multipart/form-data com o file (campo File).
     */
    async uploadCourseModuleLessonFile(
      lessonId: string,
      file: File
    ): Promise<unknown> {
      const formData = new FormData();
      formData.append('File', file);
      const url = `/${COURSE_MODULE_LESSON_PATH}/${encodeURIComponent(lessonId)}/files`;
      return api.post<unknown>(url, formData, {});
    },

    /**
     * Atualiza as perguntas selecionadas da config de quiz (PUT v1/course-module-lesson-quiz-config/{quizConfigId}/questions).
     * Usado apenas para lição quiz com Manual Question Selection. Body: { questionIds: string[] }.
     */
    async updateQuizConfigQuestions(
      quizConfigId: string,
      questionIds: string[]
    ): Promise<unknown> {
      const url = `/${COURSE_MODULE_LESSON_QUIZ_CONFIG_PATH}/${encodeURIComponent(quizConfigId)}/questions`;
      return api.put<unknown>(url, { questionIds });
    },
  };
}

export type CoursesApi = ReturnType<typeof createCoursesApi>;
